import React from 'react';
import { Flex, Box, IconButton, Badge } from '@chakra-ui/react';
import classNames from 'classnames';
import { IconNotifications } from '../../components/Icons';
import { UserMenu } from '../../components/UserMenu';
import { SearchBarAutocomplete } from '../../components/SearchBar';
import LanguageChooser from '../../components/LanguageChooser';
import { StockOwnerManagement } from './StockOwnerManagement';
import HeaderGreeting from './HeaderGreeting';
import './Header.scss';

/**
 * Header View Component
 * Pure presentation component - no business logic
 *
 * DDD Layer: UI (Presentation)
 * Responsibilities:
 * - Rendering UI elements
 * - Styling
 * - Receiving props from container component
 */
interface HeaderViewProps {
  className?: string;
  showProfile?: boolean;
  showNotifications?: boolean;
  showLanguageSelector?: boolean;
  showSearch?: boolean;
  logo?: React.ReactNode | string;
  onLogoClick?: () => void;
  navigationItems?: any[];
  userProfile?: any;
  userMenuItems?: any[];
  companyData?: any;
  isMenuOpen?: boolean;
  onProfileMenuToggle?: () => void;
  onNotificationsClick?: () => void;
  notificationsCount?: number;
  t: (key: string) => string;
  // Search props
  onSearch?: (query: string) => void;
  searchQuery?: string;
  searchResults?: any[];
  isSearchLoading?: boolean;
  fetchHeaderSuggestions?: (query: string) => Promise<any[]>;
  lang?: 'en' | 'ua';
}

const HeaderView: React.FC<HeaderViewProps> = ({
  className,
  showProfile = true,
  showNotifications = true,
  showLanguageSelector = false,
  showSearch = false,
  logo,
  onLogoClick,
  navigationItems,
  userProfile,
  userMenuItems = [],
  companyData,
  isMenuOpen,
  onProfileMenuToggle,
  onNotificationsClick,
  notificationsCount = 0,
  t,
  // Search props
  onSearch,
  searchQuery,
  searchResults,
  isSearchLoading,
  fetchHeaderSuggestions,
  lang = 'en',
}) => {
  return (
    <Flex
      as='header'
      className={classNames('app-header', className)}
      align='center'
      justify='space-between'
      pl={2}
      pr={6}
      py={3}
      bg='white'
      borderBottom='1px solid'
      borderColor='gray.200'
      position='sticky'
      top={0}
      zIndex={1000}
    >
      {/* Left: sidebar-width anchor + Greeting */}
      <Flex align='center' gap={4} flex='1'>
        <Box minWidth='200px' width='200px'>
          <StockOwnerManagement />
        </Box>
        <HeaderGreeting fontSize='md' fontWeight={500} />
      </Flex>

      {/* Navigation Items (optional) */}
      {navigationItems && navigationItems.length > 0 && (
        <Flex className='app-header__navigation' gap={4}>
          {navigationItems.map(item => (
            <Box
              key={item.id}
              className={classNames('app-header__nav-item', {
                'app-header__nav-item--active': item.isActive,
              })}
              onClick={item.onClick}
              cursor='pointer'
              px={3}
              py={2}
              borderRadius='md'
              _hover={{ bg: 'gray.100' }}
            >
              {item.icon && (
                <span className='app-header__nav-icon'>{item.icon}</span>
              )}
              <span className='app-header__nav-label'>{item.label}</span>
            </Box>
          ))}
        </Flex>
      )}
      {/* Right Section: Search, Notifications, Language, Profile */}
      <Flex align='center' gap={4} className='app-header__actions'>
        {/* Search Bar */}
        {showSearch && onSearch && fetchHeaderSuggestions && (
          <Box className='app-header__search'>
            <SearchBarAutocomplete
              placeholder='placeholder.search'
              width={250}
              height={36}
              isClearable={true}
              onSearch={onSearch}
              onSuggestionsFetch={fetchHeaderSuggestions}
              onSuggestionSelect={suggestion => {
                onSearch(suggestion.title[lang]);
              }}
              minCharsForSuggestions={2}
              maxSuggestions={5}
              debounceDelay={300}
            />
          </Box>
        )}

        {/* Notifications */}
        {showNotifications && (
          <Box position='relative'>
            <IconButton
              aria-label='Notifications'
              icon={<IconNotifications />}
              variant='ghost'
              onClick={onNotificationsClick}
              className='app-header__notification-btn'
            />
            {notificationsCount > 0 && (
              <Badge
                position='absolute'
                top='-1'
                right='-1'
                colorScheme='red'
                borderRadius='full'
                className='app-header__notification-badge'
              >
                {notificationsCount > 99 ? '99+' : notificationsCount}
              </Badge>
            )}
          </Box>
        )}

        {/* Language Selector */}
        {showLanguageSelector && (
          <Box
            className='app-header__language'
            display='flex'
            alignItems='center'
          >
            <LanguageChooser width={170} />
          </Box>
        )}

        {/* User Profile Menu */}
        {showProfile && <UserMenu user={userProfile} items={userMenuItems} />}
      </Flex>
    </Flex>
  );
};

export default HeaderView;
